import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Search, Mail, Phone, Linkedin, MapPin, Building2, RefreshCw, X, Users,
  CheckCircle2, Clock, ExternalLink, User, ShieldCheck, Award, Briefcase,
} from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import VerificationBadge from "../../components/VerificationBadge";
import IntelligenceModal from "../../components/IntelligenceModal";
import SideFilters from "../../components/SideFilters";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AVATAR_PALETTE = ["#0891B2", "#7C3AED", "#DB2777", "#059669", "#B45309", "#0369A1"];
function avatarFor(name = "") {
  const initials = name.trim().split(/\s+/).slice(0, 2).map(s => s[0] || "").join("").toUpperCase() || "?";
  let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return { initials, color: AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length] };
}

function relativeDayLabel(daysAgo) {
  if (daysAgo === 0) return "oggi";
  if (daysAgo === 1) return "ieri";
  return `${daysAgo}gg fa`;
}

// ─── Person Row / Card ────────────────────────────────────────────────────
function PersonRow({ p, onOpen }) {
  const { initials, color } = avatarFor(p.name);
  const daysAgo = p.last_updated
    ? Math.floor((Date.now() - new Date(p.last_updated).getTime()) / 86400000)
    : null;
  return (
    <button
      data-testid={`people-row-${p.id}`}
      onClick={() => onOpen(p)}
      className="w-full group text-left bg-[#0D0F12] border border-white/[0.04] hover:border-[#00E5FF]/60 hover:bg-[#0F1520] transition-all"
    >
      <div className="h-0.5 opacity-70" style={{ background: `linear-gradient(90deg, ${color}, transparent)` }} />
      <div className="p-4 flex items-center gap-4">
        <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center font-bold text-sm border" style={{ background: `${color}18`, borderColor: `${color}55`, color, fontFamily: "Chivo, sans-serif" }}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-sm font-bold text-white truncate" style={{ fontFamily: "Chivo, sans-serif" }}>{p.name}</span>
            <VerificationBadge status={p.verification_status} size="xs" profileId={p.id} />
          </div>
          <p className="text-xs text-[#CBD5E1] truncate">
            {p.title || "—"}
            {p.company && <span className="text-[#A0AEC0]"> · {p.company}</span>}
          </p>
          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#6B7280]">
            {p.email && <span className="flex items-center gap-1 truncate max-w-[220px]"><Mail className="w-3 h-3 flex-shrink-0" />{p.email}</span>}
            {p.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span>}
            {p.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.location}</span>}
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-3">
          {p.linkedin && (
            <span className="inline-flex items-center gap-1 border border-[#A78BFA]/40 bg-[#A78BFA]/10 text-[#A78BFA] px-2 py-1 text-[10px] uppercase tracking-widest font-bold">
              <Linkedin className="w-3 h-3" /> LI
            </span>
          )}
          {daysAgo !== null && (
            <span className="text-[10px] text-[#A0AEC0] tracking-widest uppercase flex items-center gap-1 hidden md:inline-flex">
              <Clock className="w-2.5 h-2.5" />{relativeDayLabel(daysAgo)}
            </span>
          )}
          <ExternalLink className="w-4 h-4 text-[#6B7280] group-hover:text-[#00E5FF] transition-colors" />
        </div>
      </div>
    </button>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Helpers: seniority detection from title
const SENIORITY_TESTS = [
  { value: "c-level",  label: "C-Level",  test: (t) => /\b(ceo|cfo|cto|coo|cmo|cro|cio|cdo|chief)\b/i.test(t || "") },
  { value: "vp",       label: "VP",       test: (t) => /\bvp\b|vice president/i.test(t || "") },
  { value: "head",     label: "Head of",  test: (t) => /\bhead\b/i.test(t || "") },
  { value: "director", label: "Director", test: (t) => /\bdirector\b|managing director/i.test(t || "") },
  { value: "manager",  label: "Manager",  test: (t) => /\bmanager\b/i.test(t || "") },
];

export default function PeopleList() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [openPerson, setOpenPerson] = useState(null);
  const [filters, setFilters] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sales/profiles`, { withCredentials: true });
      setItems(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Apply text + massive filters
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fDept       = filters.department || [];
    const fSen        = filters.seniority  || [];
    const fCompany    = (filters.company  || "").trim().toLowerCase();
    const fLocation   = (filters.location || "").trim().toLowerCase();
    const fVerif      = filters.verification || [];
    const fEmail      = filters.has_email === true;
    const fLinkedin   = filters.has_linkedin === true;
    const fVerifiedOnly = filters.verified_only === true;

    return items.filter(p => {
      if (q && ![p.name, p.company, p.title, p.email, p.location].some(v => (v || "").toLowerCase().includes(q))) return false;
      if (fDept.length && !fDept.includes(p.department)) return false;
      if (fSen.length && !fSen.some(s => SENIORITY_TESTS.find(x => x.value === s)?.test(p.title))) return false;
      if (fCompany && !(p.company || "").toLowerCase().includes(fCompany)) return false;
      if (fLocation && !(p.location || "").toLowerCase().includes(fLocation)) return false;
      if (fVerif.length && !fVerif.includes((p.verification_status || "unknown").toLowerCase())) return false;
      if (fEmail && !p.email) return false;
      if (fLinkedin && !p.linkedin) return false;
      if (fVerifiedOnly && (p.verification_status || "").toLowerCase() !== "verified") return false;
      return true;
    });
  }, [items, search, filters]);

  const facetSections = useMemo(() => {
    const deptMap = new Map();
    items.forEach(p => {
      if (!p.department) return;
      deptMap.set(p.department, (deptMap.get(p.department) || 0) + 1);
    });
    const deptOpts = Array.from(deptMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 12)
      .map(([d, n]) => ({ value: d, label: d, count: n }));

    const senOpts = SENIORITY_TESTS.map(s => ({
      value: s.value, label: s.label,
      count: items.filter(p => s.test(p.title)).length,
    })).filter(o => o.count > 0);

    const verifOpts = ["verified", "unknown", "suspicious", "fraud"].map(v => ({
      value: v, label: v.charAt(0).toUpperCase() + v.slice(1),
      count: items.filter(p => (p.verification_status || "unknown").toLowerCase() === v).length,
    })).filter(o => o.count > 0);

    return [
      { id: "department",   label: "Reparto",     icon: Briefcase, type: "checkbox", options: deptOpts },
      { id: "seniority",    label: "Seniority",   icon: Award,     type: "checkbox", options: senOpts },
      { id: "verification", label: "Verifica",    icon: ShieldCheck, type: "checkbox", options: verifOpts },
      { id: "company",      label: "Azienda",     icon: Building2, type: "text",     placeholder: "es. TechVision…" },
      { id: "location",     label: "Location",    icon: MapPin,    type: "text",     placeholder: "es. Milano, Berlin…" },
      { id: "verified_only",label: "Solo verificati", icon: ShieldCheck, type: "toggle" },
      { id: "has_email",    label: "Con email",   icon: Mail,      type: "toggle" },
      { id: "has_linkedin", label: "Con LinkedIn", icon: Linkedin, type: "toggle" },
    ];
  }, [items]);

  const kpis = useMemo(() => {
    const verified = items.filter(p => (p.verification_status || "").toLowerCase() === "verified").length;
    const withEmail = items.filter(p => !!p.email).length;
    const withLinkedin = items.filter(p => !!p.linkedin).length;
    return { total: items.length, verified, withEmail, withLinkedin };
  }, [items]);

  return (
    <div className="p-4 sm:p-6 space-y-6 min-h-full">
      {/* Header (full-width) */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 mb-2 text-[10px] tracking-[0.25em] uppercase text-[#00E5FF] font-bold">
            <User className="w-3 h-3" /> Intelligence · People List
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>People List</h1>
          <p className="text-sm text-[#A0AEC0] mt-1 max-w-2xl">
            Tutte le persone salvate dal tuo tenant. Click su una riga per aprire la scheda completa con OSINT · SOCMINT · HUMINT unificati.
          </p>
        </div>
        <button data-testid="refresh-people-list" onClick={load} className="flex items-center gap-1.5 bg-[#1F242D] text-white border border-white/[0.06] px-3 py-2 text-xs hover:bg-[#30363D] transition-colors">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </button>
      </div>

      {/* KPI bar (full-width) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Totale profili", value: kpis.total, color: "#0891B2", icon: Users },
          { label: "Verificati", value: kpis.verified, color: "#059669", icon: CheckCircle2 },
          { label: "Con Email", value: kpis.withEmail, color: "#B45309", icon: Mail },
          { label: "Con LinkedIn", value: kpis.withLinkedin, color: "#7C3AED", icon: Linkedin },
        ].map(kpi => (
          <div key={kpi.label} className="border border-white/[0.04] bg-[#0D0F12] p-4 flex items-center justify-between">
            <div>
              <p className="text-[9px] tracking-[0.2em] uppercase text-[#A0AEC0] font-semibold mb-1">{kpi.label}</p>
              <p className="text-2xl font-bold tabular-nums" style={{ color: kpi.color, fontFamily: "Chivo, sans-serif" }}>{kpi.value}</p>
            </div>
            <kpi.icon className="w-5 h-5" style={{ color: kpi.color }} />
          </div>
        ))}
      </div>

      {/* Body: sidebar filtri + main (search + lista) */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Sidebar filtri laterali (altezza pari al search bar) */}
        <SideFilters
          open={filtersOpen} setOpen={setFiltersOpen}
          filters={filters} setFilters={setFilters}
          sections={facetSections}
          totalMatch={filtered.length} totalAll={items.length}
          entity="people"
        />

        {/* Main: search + lista */}
        <div className="flex-1 min-w-0 space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
        <input
          data-testid="people-list-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filtra per nome, azienda, ruolo, email…"
          className="w-full bg-[#050505] border border-white/[0.04] text-white pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:border-[#00E5FF] transition-colors placeholder:text-[#6B7280]"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6B7280] hover:text-white transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center text-[#A0AEC0] py-16 tracking-widest uppercase text-xs">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-white/[0.04] bg-[#0D0F12] py-16 text-center">
          <User className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-white font-semibold">Nessuna persona salvata</p>
          <p className="text-xs text-[#A0AEC0] mt-1">Vai su <span className="text-[#00E5FF]">People Check</span> per cercare e aggiungere profili al tuo database.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => <PersonRow key={p.id} p={p} onOpen={setOpenPerson} />)}
        </div>
      )}

      {/* Detail modal */}
      {openPerson && (
        <IntelligenceModal
          targetId={openPerson.id}
          targetType="person"
          title={openPerson.name}
          subtitle={[openPerson.title, openPerson.company].filter(Boolean).join(" · ")}
          onClose={() => setOpenPerson(null)}
        >
          {/* Identity data section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            {[
              ["Email", openPerson.email, Mail],
              ["Telefono", openPerson.phone, Phone],
              ["LinkedIn", openPerson.linkedin, Linkedin],
              ["Sede", openPerson.location, MapPin],
              ["Reparto", openPerson.department, Building2],
              ["Azienda", openPerson.company, Building2],
            ].filter(([, v]) => v).map(([label, value, Icon]) => (
              <div key={label} className="flex items-start gap-2 text-xs">
                <Icon className="w-3.5 h-3.5 text-[#00E5FF] mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] tracking-widest uppercase text-[#A0AEC0]">{label}</p>
                  <p className="text-white truncate">{value}</p>
                </div>
              </div>
            ))}
            <div className="flex items-start gap-2 text-xs">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#16C784] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] tracking-widest uppercase text-[#A0AEC0]">Verifica identità</p>
                <p className="text-white capitalize">{openPerson.verification_status || "unknown"}</p>
              </div>
            </div>
          </div>
        </IntelligenceModal>
      )}
        </div>{/* /main search+lista */}
      </div>{/* /body flex sidebar+main */}
    </div>
  );
}
